import { SortablePortfolioItem } from '@unistocks/features/portfolios/components/portfolio-settings/sort/types';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { SortSetting } from '@unistocks/proxy/axios-client';

export type SortingAction =
  | { type: 'INIT'; portfolio: PortfolioBase }
  | { type: 'TOGGLE_MODE' }
  | { type: 'TOGGLE_AUTO_SORT'; sectionId: string }
  | { type: 'SET_SORT_ORDER'; sortOrder: string[] }
  | { type: 'SET_SECTION_ORDER'; sectionOrder: string[] };

export type SortingSection = {
  id: string;
  name: string;
  sorting: SortSetting;
  originalProperty: string | null;
  assetIds: string[];
};

export type SortingState = {
  mode: 'categories' | 'holdings';
  /**
   * Sections in their current order, each holding the order of assets assigned to it.
   */
  sections: SortingSection[];
  portfolio: PortfolioBase;
  data: SortablePortfolioItem[];
};

const createSections = (portfolio: PortfolioBase): SortingSection[] =>
  portfolio.sections.map(section => ({
    id: section.id,
    name: section.name,
    sorting: { ...section.sorting },
    originalProperty: section.sorting.property,
    assetIds: section.assets.map(asset => asset.id)
  }));

const createSortableData = ({
  mode,
  sections,
  portfolio
}: Pick<SortingState, 'mode' | 'sections' | 'portfolio'>): SortingState['data'] => {
  const assetsById = new Map(
    portfolio.sections.flatMap(section => section.assets.map(asset => [asset.id, asset]))
  );
  const items: SortablePortfolioItem[] = [];

  for (const section of sections) {
    items.push({
      type: 'section',
      id: section.id,
      item: {
        id: section.id,
        name: section.name,
        sorting: section.sorting
      }
    });

    if (mode === 'holdings') {
      for (const assetId of section.assetIds) {
        const asset = assetsById.get(assetId);

        if (asset) {
          items.push({ type: 'asset', id: asset.id, item: asset });
        }
      }
    }
  }

  return items;
};

/**
 * Rebuilds the sections from a flat order of section and asset ids.
 * Assets are assigned to the section they follow in the flat order, which
 * preserves cross-section moves made by dragging assets between sections.
 */
const assignAssetsBySection = (sections: SortingSection[], sortOrder: string[]): SortingSection[] => {
  const sectionById = new Map(sections.map(section => [section.id, section]));
  const sectionIds = new Set(sectionById.keys());
  const assetsBySectionId = new Map<string, string[]>();

  let currentSectionId: string | undefined;
  for (const id of sortOrder) {
    if (sectionIds.has(id)) {
      currentSectionId = id;
      assetsBySectionId.set(id, []);
    } else if (currentSectionId) {
      assetsBySectionId.get(currentSectionId)?.push(id);
    }
  }

  return sortOrder
    .filter(id => sectionIds.has(id))
    .map(id => sectionById.get(id))
    .filter((section): section is SortingSection => !!section)
    .map(section => ({ ...section, assetIds: assetsBySectionId.get(section.id) ?? [] }));
};

export const sortingReducer = (state: SortingState, action: SortingAction): SortingState => {
  switch (action.type) {
    case 'INIT': {
      const initialized = {
        ...state,
        mode: 'holdings' as const,
        portfolio: action.portfolio,
        sections: createSections(action.portfolio)
      };

      return {
        ...initialized,
        data: createSortableData(initialized)
      };
    }

    case 'TOGGLE_MODE': {
      const toggled = {
        ...state,
        mode: state.mode === 'holdings' ? ('categories' as const) : ('holdings' as const)
      };

      return {
        ...toggled,
        data: createSortableData(toggled)
      };
    }

    case 'TOGGLE_AUTO_SORT': {
      const toggledSections = state.sections.map(section =>
        section.id === action.sectionId
          ? {
              ...section,
              sorting: {
                ...section.sorting,
                property:
                  section.sorting.property === null
                    ? (section.originalProperty ?? 'name')
                    : null
              }
            }
          : section
      );
      const autoSorted = { ...state, sections: toggledSections };

      return {
        ...autoSorted,
        data: createSortableData(autoSorted)
      };
    }

    case 'SET_SECTION_ORDER': {
      const sectionById = new Map(state.sections.map(section => [section.id, section]));
      const sections = action.sectionOrder
        .map(sectionId => sectionById.get(sectionId))
        .filter((section): section is SortingSection => !!section);
      const reordered = { ...state, sections };

      return {
        ...reordered,
        data: createSortableData(reordered)
      };
    }

    case 'SET_SORT_ORDER': {
      const sorted = {
        ...state,
        sections: assignAssetsBySection(state.sections, action.sortOrder)
      };

      return {
        ...sorted,
        data: createSortableData(sorted)
      };
    }

    default:
      return state;
  }
};